// TODO: CONTINUE REFACTOR

import { JSX, ReactNode, useEffect, useState } from "react";
import { CloseOutlined, EllipsisOutlined } from "@ant-design/icons";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Button, Dropdown, Menu, Tag, Typography } from "antd";
import clsx from "clsx";
import {
  Outlet,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import "./project-journals.scss";
import { useObject } from "@/entities/object/index.ts";
import { projectQueries } from "@/entities/project/index.ts";
import { JournalIntegrationTags } from "@/features/journal-integration-tags";
import { JournalTabs } from "@/features/journal-tabs/ui";
import type { JournalVersion } from "@/features/journal-version-list";
import {
  useAmendmentsStore,
  useJournalVersionsStore,
  useTitleVersionsStore,
} from "@/features/journal-version-list";
import { VersionSwitcherDropdown } from "@/features/journal-version-switcher";
import { JournalsAmendmentsStatusBadge } from "@/pages/journals";
import { HistoryTimeIcon, SettingsIcon } from "@/shared/assets/svg/icons";
import { useFlags } from "@/shared/lib/flags";
import { useCoordinator, useNavigationPosition } from "@/shared/lib/hooks";
import { routes, useNavigate } from "@/shared/lib/react-router";
import {
  journalsQueries,
  SignStatusTypes,
  JournalInfoType,
  STATUS_MAP,
} from "@entities/journal";
import {
  Can,
  EntityBaseAbilityBuilder,
  useUpdateAbility,
} from "@shared/lib/casl";
import { Breadcrumbs } from "@shared/ui";
import { isUnifiedRoute } from "@shared/utils";
import { JournalBanner } from "./journal-banner/ui/journal-banner/journal-banner.tsx";
import { JournalsSettings } from "./journals-settings";
import {
  OjrFormationPanelProvider,
  OjrFormationPanelTopRow,
  OjrFormationPanelContent,
} from "./ojr-formation-panel";

const { Title } = Typography;

export const ProjectJournalsView = () => {
  const [isSettings, setIsSettings] = useState<boolean>(false);

  const {
    projectId,
    titleId,
    objectId,
    mainJournalId,
    mainJournalVersionId,
    titleVersionId,
  } = useCoordinator() as unknown as {
    projectId: string;
    titleId: string;
    objectId: string;
    mainJournalId: string;
    mainJournalVersionId: string;
    titleVersionId: string;
  };
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isTitleAmendmentsForm,
    isTitleAmendmentsViewport,
    isJournalVersionTitle,
    isTitleVersionDetail,
  } = useNavigationPosition();

  const isVersionsEnabled = useFlags("ojrFormationFlowEnabledV1");

  const [params] = useSearchParams();
  const { requestId } = useParams<{ requestId?: string }>();
  const amendmentRequests = useAmendmentsStore((s) => s.requests);
  const currentAmendment = requestId
    ? amendmentRequests.find((r) => r.key === requestId)
    : null;

  const { data: project } = useSuspenseQuery(
    projectQueries.getProject({ projectId }),
  );

  const { data: object } = useObject({ objectId });
  const handleSetSetting = (isSetting: boolean) => () =>
    setIsSettings(isSetting);

  const journalVersions = useJournalVersionsStore(
    (s: { versions: JournalVersion[] }) => s.versions,
  );
  const titleVersions = useTitleVersionsStore(
    (s: { versions: JournalVersion[] }) => s.versions,
  );

  const journalVersionForBreadcrumb = mainJournalVersionId
    ? journalVersions.find(
        (v: JournalVersion) => v.versionId === mainJournalVersionId,
      )
    : null;

  const generateBreadcrumbs = (
    projectId: string,
    projectShortName: string,
    objectShortName: string,
  ) => {
    const base = [
      { path: routes.projects(), label: "Проекты" },
      {
        path: routes.project.root({ projectId }),
        label: projectShortName,
      },
      { path: routes.objects.root(projectId), label: "Объекты" },
      {
        path: routes.objects.object(projectId, objectId),
        label: objectShortName,
      },
      { path: routes.objects.journals(projectId, objectId), label: "Журналы" },
    ];

    if (isJournalVersionTitle) {
      return [
        ...base,
        {
          path: routes.journals.versions(
            projectId,
            objectId,
            mainJournalId,
            titleId,
          ),
          label: "Версии ОЖР",
        },
        {
          path: routes.journals.journalVersionTitle(
            projectId,
            objectId,
            mainJournalId,
            titleId,
            mainJournalVersionId,
          ),
          label: journalVersionForBreadcrumb
            ? `Версия ${journalVersionForBreadcrumb.versionNumber}`
            : "Версия",
        },
        {
          path: routes.journals.journalVersionTitle(
            projectId,
            objectId,
            mainJournalId,
            titleId,
            mainJournalVersionId,
          ),
          label: "Титульный лист",
        },
      ];
    }

    return base;
  };

  const menu = (
    <Menu>
      <Can I="admin" a="main_journals">
        <Menu.Item
          key="4"
          icon={<SettingsIcon />}
          onClick={handleSetSetting(true)}
        >
          Настройки
        </Menu.Item>
      </Can>

      <Can not I="admin" a="main_journals">
        <Can I="access" a="main_journals">
          <Menu.Item
            key="4"
            icon={<SettingsIcon />}
            onClick={handleSetSetting(true)}
          >
            Настройки
          </Menu.Item>
        </Can>
      </Can>

      {params.get("tab") === "notes" && (
        <Menu.SubMenu
          key="notes"
          title={
            <Menu.Item
              key="3"
              icon={(<i className="icon-xml" />) as JSX.Element}
            >
              Скачать XML
            </Menu.Item>
          }
        >
          <Menu.Item key="notes-1">%наим документа% + %дата%.xml</Menu.Item>
          <Menu.Item key="notes-2">%наим документа% + %дата%.xml</Menu.Item>
          <Menu.Item key="notes-3">%наим документа% + %дата%.xml</Menu.Item>
          <Menu.Item key="notes-4">%наим документа% + %дата%.xml</Menu.Item>
          <Menu.Item key="notes-5">%наим документа% + %дата%.xml</Menu.Item>
        </Menu.SubMenu>
      )}

      {isVersionsEnabled && location.pathname.includes("titleJournal") && (
        <>
          <Menu.Item
            key="version"
            icon={<HistoryTimeIcon />}
            onClick={() =>
              navigate(
                routes.journals.versions(
                  projectId,
                  objectId,
                  mainJournalId,
                  titleId,
                ),
              )
            }
          >
            Просмотр версии
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const { data: journalTitle } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const { data: mainJournalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  useEffect(() => {
    setIsSettings(false);
  }, [location.pathname, location.search]);

  useUpdateAbility({
    // @ts-expect-error я тебя ненавиж
    permissions: journalTitle?.mainJournal?.titleJournal?.permissions,
    abilityBuilder: EntityBaseAbilityBuilder(),
  });

  useUpdateAbility({
    // @ts-expect-error permissions из mainJournal не совпадает по типам
    permissions: mainJournalData?.mainJournal?.permissions,
    abilityBuilder: EntityBaseAbilityBuilder(),
  });

  const [journalInfo, setJournalInfo] = useState<JournalInfoType>({
    message: {
      color: "#4169e1",
      messageText:
        "Вас назначили %НазваниеРоли в объект %ИмяОбъекта. Подпишите назначение на Титульном листе Журнала общих работ",
      textButton: "Посмотреть",
      buttonLogic: () => {
        // TODO: implement action
      },
    },
    status: {
      prevStatusText: "Отклонен",
      prevStatusColor: "white",
      prevStatusBackground: "red",
      statusText: "Отклонен",
      statusColor: "#FFFFFF",
      statusBackground: "#F20404",
    },
    isSignedTables: false,
    isHideButtons: false,
    isHideTableInfo: false,
  });

  const currentStatus =
    STATUS_MAP[journalTitle?.mainJournal?.status as keyof typeof STATUS_MAP] ??
    STATUS_MAP.NEW;

  const isUnified = isUnifiedRoute(location.pathname);
  const [signStatus, setSignStatus] = useState(SignStatusTypes.onSign);

  const isTitleJournalRoute = location.pathname.includes("titleJournal");
  const isJournalVersionMode =
    location.pathname.includes("/versions/") &&
    /\/versions\/[^/]+\/[^/]+/.test(location.pathname);
  const versionSectionMatch = location.pathname.match(/\/section([1-6])\//);
  const versionSectionNumber = versionSectionMatch
    ? versionSectionMatch[1]
    : null;
  const getNextJournalVersionPath = (nextVersionId: string) => {
    if (isJournalVersionMode) {
      switch (versionSectionNumber) {
        case "1":
          return routes.journals.journalVersionSection1(
            projectId,
            objectId,
            mainJournalId,
            titleId,
            nextVersionId,
          );
        case "2":
          return routes.journals.journalVersionSection2(
            projectId,
            objectId,
            mainJournalId,
            titleId,
            nextVersionId,
          );
        case "3":
          return routes.journals.journalVersionSection3(
            projectId,
            objectId,
            mainJournalId,
            titleId,
            nextVersionId,
          );
        case "4":
          return routes.journals.journalVersionSection4(
            projectId,
            objectId,
            mainJournalId,
            titleId,
            nextVersionId,
          );
        case "5":
          return routes.journals.journalVersionSection5(
            projectId,
            objectId,
            mainJournalId,
            titleId,
            nextVersionId,
          );
        case "6":
          return routes.journals.journalVersionSection6(
            projectId,
            objectId,
            mainJournalId,
            titleId,
            nextVersionId,
          );
        default:
          break;
      }
    }

    return routes.journals.journalVersionTitle(
      projectId,
      objectId,
      mainJournalId,
      titleId,
      nextVersionId,
    );
  };

  const currentJournalVersion = journalVersions.find(
    (v: JournalVersion) => v.versionId === mainJournalVersionId,
  );
  const currentTitleVersion = titleVersions.find(
    (v: JournalVersion) => v.versionId === titleVersionId,
  );

  const isJournalVersionInactive =
    isJournalVersionMode &&
    !!currentJournalVersion &&
    currentJournalVersion.status !== "ACTUAL";

  const isTitleVersionInactive =
    isTitleVersionDetail &&
    !!currentTitleVersion &&
    currentTitleVersion.status !== "ACTUAL";

  const isJournalInactiveVersionView =
    isJournalVersionInactive || isTitleVersionInactive;

  const generalViewClassName = [
    "general-view-container",
    isJournalInactiveVersionView
      ? "general-view-container--journal-inactive-version"
      : "",
    isTitleAmendmentsViewport ? "journals-amendments-page__viewport" : "",
    isTitleAmendmentsForm && currentAmendment?.requestSource === "SYSTEM"
      ? "journals-amendments-page__viewport--system"
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  const isOjrFormationFlowEnabled = useFlags("ojrFormationFlowEnabledV1");

  const journalTitleText = (
    <>
      Общий журнал работ{" "}
      {journalTitle?.mainJournal?.titleJournal?.mainJournalNumber ? (
        <>№{journalTitle.mainJournal.titleJournal.mainJournalNumber}</>
      ) : null}
    </>
  );

  const titleRowMain = isTitleVersionDetail ? (
    <>
      <Title
        level={3}
        style={{
          marginBottom: "0px",
          color: "rgba(119, 119, 119, 1)",
          cursor: "pointer",
        }}
        onClick={() =>
          navigate(
            routes.journals.title(projectId, objectId, mainJournalId, titleId),
          )
        }
      >
        {journalTitleText}
      </Title>
      <span className="project-journals-title-separator">·</span>
      <Title level={3} style={{ marginBottom: "0px" }}>
        Титульный лист
      </Title>
      <JournalIntegrationTags variant="inline" />
      {isVersionsEnabled && (
        <div className="project-journals-version-switcher">
          <VersionSwitcherDropdown
            versions={titleVersions}
            selectedVersionId={currentTitleVersion?.versionId ?? titleVersionId}
            onVersionSelect={(v) =>
              navigate(
                routes.journals.titleVersionDetail(
                  projectId,
                  objectId,
                  mainJournalId,
                  titleId,
                  v.versionId,
                ),
              )
            }
          />
        </div>
      )}
    </>
  ) : (
    <>
      <Title level={3} style={{ marginBottom: "0px" }}>
        {journalTitleText}
      </Title>
      <div
        className={clsx(
          "project-journals-status-badge",
          journalTitle?.mainJournal?.status &&
            `project-journals-status-badge--${journalTitle.mainJournal.status}`,
        )}
      >
        {currentStatus?.label}
      </div>
      <JournalIntegrationTags variant="inline" />
      {isVersionsEnabled && (isTitleJournalRoute || isJournalVersionMode) ? (
        <div className="project-journals-version-switcher">
          <VersionSwitcherDropdown
            versions={journalVersions}
            selectedVersionId={
              mainJournalVersionId || journalVersions[0]?.versionId || ""
            }
            onVersionSelect={(v) =>
              navigate(getNextJournalVersionPath(v.versionId))
            }
          />
        </div>
      ) : null}
    </>
  );

  const titleRow = isTitleAmendmentsForm ? (
    <div className="project-journals-amendments-title">
      <Title level={3} style={{ marginBottom: 0 }}>
        {currentAmendment?.requestSource === "SYSTEM"
          ? "Заявка на обновление реквизитов организации"
          : "Заявка на изменение титульного листа"}
      </Title>
      <Tag className="project-journals-amendments-version">
        Версия №{currentAmendment?.versionNumber ?? "—"}
      </Tag>
      {currentAmendment?.status ? (
        <JournalsAmendmentsStatusBadge status={currentAmendment.status} />
      ) : null}
    </div>
  ) : (
    titleRowMain
  );

  const closeAmendmentsFormButton = (
    <Button
      shape="circle"
      type="text"
      icon={<CloseOutlined />}
      aria-label="Закрыть"
      onClick={() =>
        navigate(
          routes.journals.amendments(
            projectId,
            objectId,
            mainJournalId,
            titleId,
          ),
        )
      }
      style={{
        borderRadius: "10px",
      }}
    />
  );

  const headerMenuDropdown = (
    <Dropdown overlay={menu} trigger={["hover"]}>
      <Button
        shape="circle"
        icon={(<EllipsisOutlined />) as ReactNode}
        style={{
          borderRadius: "10px",
        }}
      />
    </Dropdown>
  );

  return (
    <div className={generalViewClassName}>
      <JournalBanner />
      <Breadcrumbs
        routes={generateBreadcrumbs(
          project.projectId,
          project.shortName,
          object?.shortName ?? "",
        )}
        disableFirst={isUnified ? 3 : 0}
      />
      {isOjrFormationFlowEnabled ? (
        <OjrFormationPanelProvider
          titleId={titleId}
          objectId={objectId}
          mainJournalId={mainJournalId}
          titleStatus={journalTitle?.mainJournal?.titleJournal?.status}
        >
          <div className="table-journals-wrapper">
            <div className="table-journals-wrapper-title">
              <div className="table-journals-wrapper-title-row">{titleRow}</div>
              {isTitleAmendmentsForm ? null : (
                <JournalIntegrationTags variant="banner" />
              )}
            </div>
            <div className="table-journals-wrapper-actions">
              {!isTitleAmendmentsForm ? <OjrFormationPanelTopRow /> : null}
              {isTitleAmendmentsForm
                ? closeAmendmentsFormButton
                : headerMenuDropdown}
            </div>
          </div>
          {!isTitleAmendmentsForm ? (
            <div className="table-journals-ojr">
              <OjrFormationPanelContent />
            </div>
          ) : null}
        </OjrFormationPanelProvider>
      ) : (
        <div className="table-journals-wrapper">
          <div className="table-journals-wrapper-title">
            <div className="table-journals-wrapper-title-row">{titleRow}</div>
            {isTitleAmendmentsForm ? null : (
              <JournalIntegrationTags variant="banner" />
            )}
          </div>
          {isTitleAmendmentsForm
            ? closeAmendmentsFormButton
            : headerMenuDropdown}
        </div>
      )}
      {isTitleAmendmentsForm ? null : <JournalTabs />}
      {isSettings ? (
        <JournalsSettings goBack={handleSetSetting(false)} />
      ) : (
        <Outlet
          context={{ journalInfo, setJournalInfo, signStatus, setSignStatus }}
        />
      )}
    </div>
  );
};
